/**
 * WalkCompanion Firebase Service
 * Cross-platform Firebase service for data operations
 */

import {
  getDatabase,
  ref,
  get,
  set,
  update,
  remove,
  onValue,
  off,
  DatabaseReference,
  Database
} from 'firebase/database';
import { FirebaseApp, initializeApp, getApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import {
  Challenge,
  User,
  ChallengeParticipation,
  Leaderboard,
  Organization,
  Team,
  HealthData,
  ChallengeWithEligibility,
  ChallengeLevel,
  FirebaseConfig,
  DailyCompletion
} from '../types';
import { logger } from '../config/loggingConfig';
import { challengePaths, databasePaths } from '../config/environmentConfig';

// Utility to sanitize Firebase keys
export function sanitizeFirebaseKey(key: string): string {
  return key.replace(/[.#$\[\]]/g, '_');
}

export class FirebaseService {
  private app: FirebaseApp;
  private database: Database;
  private ref: DatabaseReference;
  private listeners: Map<string, () => void> = new Map();
  private isDebugMode: boolean;
  private environment: 'dev' | 'prod';

  constructor(config?: FirebaseConfig, environment: 'dev' | 'prod' = 'dev') {
    this.environment = environment;
    // Check if we're in debug mode (web app only)
    this.isDebugMode = typeof window !== 'undefined' && (process.env.REACT_APP_DEBUG === 'true' || process.env.REACT_APP_DEBUG === '1');

    // Always use the existing app instance to ensure auth state is shared
    if (config) {
      // For web, try to get existing app first, then create new one if needed
      try {
        this.app = getApp();
      } catch (error) {
        // No existing app, create new one
        this.app = initializeApp(config);
      }
    } else {
      this.app = getApp();
    }
    this.database = getDatabase(this.app);
    this.ref = ref(this.database);

    // Debug: Check authentication state
    try {
      const auth = getAuth(this.app);
      if (this.isDebugMode) {
        console.log('🔐 FirebaseService: Firebase app initialized with auth:', auth);
      } else {
        console.log('🔐 FirebaseService: Firebase app initialized with auth: [details hidden - set REACT_APP_DEBUG=true to show]');
      }
    } catch (error) {
      console.log('⚠️ FirebaseService: Could not get auth instance:', error);
    }
  }

  // ============================================================================
  // CHALLENGE OPERATIONS
  // ============================================================================

  /**
   * Load all challenges for a user based on their eligibility
   */
  async loadUserChallenges(userId: string, user?: User): Promise<ChallengeWithEligibility[]> {
    try {
      if (!user) {
        logger.general.warning('No user data provided, cannot load challenges');
        return [];
      }

      console.log('[FirebaseService] loadUserChallenges - user object received:', {
        id: user.id,
        email: user.email,
        organizationId: user.organizationId,
        familyStatus: user.familyStatus,
        familyMemberOf: user.familyMemberOf
      });

      const challenges: ChallengeWithEligibility[] = [];

      // Load challenges based on user role and organization
      try {
        // Log environment info for debugging
        console.log('🔍 [FirebaseService] Environment:', {
          hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
          environment: this.environment,
          databasePaths: {
            participations: databasePaths.participations(this.environment),
            challenges: databasePaths.challenges(this.environment),
            users: databasePaths.users(this.environment)
          }
        });

        // Always load global challenges (available to all authenticated users)
        const globalChallengesRef = ref(this.database, challengePaths.global(this.environment));
        const globalSnapshot = await get(globalChallengesRef);

        if (globalSnapshot.exists()) {
          const globalData = globalSnapshot.val();

          for (const challengeId in globalData) {
            const challengeData = globalData[challengeId];
            const challenge: Challenge = {
              id: challengeId,
              ...challengeData
            };

            // Check eligibility
            const eligibility = this.checkChallengeEligibility(challenge, user);
            const challengeWithEligibility: ChallengeWithEligibility = {
              ...challenge,
              isEligible: eligibility.isEligible,
              eligibilityReason: eligibility.reason
            };

            challenges.push(challengeWithEligibility);
          }
        }
      } catch (error) {
        logger.general.warning('Could not load global challenges:', error);
      }

      // Always load personal challenges (available to all authenticated users)
      try {
        const personalChallengesRef = ref(this.database, challengePaths.personal(this.environment));
        const personalSnapshot = await get(personalChallengesRef);

        if (personalSnapshot.exists()) {
          const personalData = personalSnapshot.val();

          for (const challengeId in personalData) {
            const challengeData = personalData[challengeId];
            const challenge: Challenge = {
              id: challengeId,
              ...challengeData
            };

            const eligibility = this.checkChallengeEligibility(challenge, user);
            const challengeWithEligibility: ChallengeWithEligibility = {
              ...challenge,
              isEligible: eligibility.isEligible,
              eligibilityReason: eligibility.reason
            };

            challenges.push(challengeWithEligibility);
          }
        }
      } catch (error) {
        logger.general.warning('Could not load personal challenges:', error);
      }

      // Load organization challenges - only show if user belongs to that organization
      try {
        const orgChallengesRef = ref(this.database, challengePaths.organization(this.environment));
        const orgSnapshot = await get(orgChallengesRef);

        if (orgSnapshot.exists()) {
          const orgData = orgSnapshot.val();

          for (const challengeId in orgData) {
            const challengeData = orgData[challengeId];
            const challenge: Challenge = {
              id: challengeId,
              ...challengeData
            };

            // Include challenges for user's organization OR if user is a family member of that organization
            const isUserOrganization = challenge.organizationId === user.organizationId;
            let isFamilyMemberOfOrganization = false;

            console.log('[FirebaseService] loadUserChallenges - checking challenge:', challengeId, {
              challengeOrgId: challenge.organizationId,
              userOrgId: user.organizationId,
              isUserOrganization,
              userFamilyStatus: user.familyStatus,
              userFamilyMemberOf: user.familyMemberOf
            });

            if (user.familyStatus === 'family_member' && user.familyMemberOf && challenge.organizationId) {
              console.log('[FirebaseService] loadUserChallenges - checking family member organization access');
              isFamilyMemberOfOrganization = await this.isFamilyMemberOfOrganization(user.id, user.familyMemberOf, challenge.organizationId);
              console.log('[FirebaseService] loadUserChallenges - family member organization access result:', isFamilyMemberOfOrganization);
            }

            console.log('[FirebaseService] loadUserChallenges - final decision:', {
              isUserOrganization,
              isFamilyMemberOfOrganization,
              willInclude: isUserOrganization || isFamilyMemberOfOrganization
            });

            if (isUserOrganization || isFamilyMemberOfOrganization) {
              const eligibility = this.checkChallengeEligibility(challenge, user);
              const challengeWithEligibility: ChallengeWithEligibility = {
                ...challenge,
                isEligible: eligibility.isEligible,
                eligibilityReason: eligibility.reason
              };

              challenges.push(challengeWithEligibility);
            }
          }
        }
      } catch (error) {
        logger.general.warning('Could not load organization challenges:', error);
      }

      // Load team challenges - only show if user belongs to that team
      try {
        const teamChallengesRef = ref(this.database, challengePaths.team(this.environment));
        const teamSnapshot = await get(teamChallengesRef);

        if (teamSnapshot.exists()) {
          const teamData = teamSnapshot.val();

          for (const challengeId in teamData) {
            const challengeData = teamData[challengeId];
            const challenge: Challenge = {
              id: challengeId,
              ...challengeData
            };

            // Only include challenges for user's team
            if (challenge.teamId === user.teamId) {
              const eligibility = this.checkChallengeEligibility(challenge, user);
              const challengeWithEligibility: ChallengeWithEligibility = {
                ...challenge,
                isEligible: eligibility.isEligible,
                eligibilityReason: eligibility.reason
              };

              challenges.push(challengeWithEligibility);
            }
          }
        }
      } catch (error) {
        logger.general.warning('Could not load team challenges:', error);
      }

      return challenges;

    } catch (error) {
      console.error('❌ Error loading challenges:', error);
      throw new Error(`Failed to load challenges: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if a user is eligible for a challenge
   */
  checkChallengeEligibility(challenge: Challenge, user?: User): { isEligible: boolean; reason?: string } {
    if (!user) {
      return { isEligible: false, reason: 'User not authenticated' };
    }

    // Check if challenge is active
    if (!challenge.isActive) {
      return { isEligible: false, reason: 'Challenge is not active' };
    }

    // Check date eligibility
    const now = new Date();
    const startDate = new Date(challenge.startDate);
    const endDate = new Date(challenge.endDate);

    if (now < startDate) {
      return { isEligible: false, reason: 'Challenge has not started yet' };
    }

    if (now > endDate) {
      return { isEligible: false, reason: 'Challenge has ended' };
    }

    // Check organization eligibility
    if (challenge.level === 'organization' && challenge.organizationId) {
      if (user.organizationId !== challenge.organizationId) {
        return { isEligible: false, reason: 'Challenge is for a different organization' };
      }
    }

    // Check team eligibility
    if (challenge.level === 'team' && challenge.teamId) {
      if (user.teamId !== challenge.teamId) {
        return { isEligible: false, reason: 'Challenge is for a different team' };
      }
    }

    return { isEligible: true };
  }

  /**
   * Join a challenge
   */
  async joinChallenge(userId: string, challengeId: string, user?: User): Promise<void> {
    try {
      console.log(`🔄 User ${userId} joining challenge ${challengeId}`);

      // Debug: Check authentication state
      const auth = getAuth(this.app);
      const currentUser = auth.currentUser;
      console.log('🔐 Firebase Auth state:', {
        currentUser: currentUser ? currentUser.uid : 'null',
        userId: userId,
        isAuthenticated: !!currentUser
      });

      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // First, get the challenge and validate eligibility
      const challenge = await this.getChallenge(challengeId);
      if (!challenge) {
        throw new Error('Challenge not found');
      }

      // Check if challenge is active
      if (!challenge.isActive) {
        throw new Error('Challenge is not active');
      }

      // Check if challenge has ended
      const now = new Date();
      const endDate = new Date(challenge.endDate);
      if (now > endDate) {
        throw new Error('Challenge has ended');
      }

      // Check if challenge has started
      const startDate = new Date(challenge.startDate);
      if (now < startDate) {
        throw new Error('Challenge has not started yet');
      }

      // If user data is provided, check eligibility
      if (user) {
        const eligibility = this.checkChallengeEligibility(challenge, user);
        if (!eligibility.isEligible) {
          throw new Error(eligibility.reason || 'You are not eligible for this challenge');
        }
      }

      // Check if user is already participating
      const existingParticipation = await this.getParticipation(userId, challengeId);
      if (existingParticipation) {
        // Allow rejoining if the participation was abandoned
        if (existingParticipation.status === 'abandoned') {
          console.log('🔄 User has abandoned participation, allowing rejoin');
          // Remove the abandoned participation to allow fresh join
          const participationRef = ref(this.database, `participations/${userId}/${challengeId}`);
          await remove(participationRef);
        } else {
          throw new Error('You are already participating in this challenge');
        }
      }

      // Use original userId (without sanitization) for participations to match database rules
      const participationRef = ref(this.database, `participations/${userId}/${challengeId}`);
      console.log('📝 Writing to Firebase path:', `participations/${userId}/${challengeId}`);

      const participation: ChallengeParticipation = {
        challengeId,
        userId,
        status: 'in_progress',
        currentValue: 0,
        progress: 0,
        pointsEarned: 0,
        startedAt: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
        lastUpdated: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z')
      };

      console.log('📝 Participation data to write:', participation);

      await set(participationRef, participation);
      console.log(`✅ User ${userId} successfully joined challenge ${challengeId}`);

    } catch (error) {
      console.error('❌ Error joining challenge:', error);
      throw new Error(`Failed to join challenge: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update challenge progress
   */
  async updateChallengeProgress(
    userId: string,
    challengeId: string,
    value: number,
    isDailyCompletion: boolean = false
  ): Promise<void> {
    try {
      console.log(`🔄 Updating progress for user ${userId}, challenge ${challengeId}: ${value}`);

      // Use original userId (without sanitization) for participations to match database rules
      const participationRef = ref(this.database, `participations/${userId}/${challengeId}`);
      const snapshot = await get(participationRef);

      if (!snapshot.exists()) {
        throw new Error('Participation not found');
      }

      const participation: ChallengeParticipation = snapshot.val();
      const challenge = await this.getChallenge(challengeId);

      if (!challenge) {
        throw new Error('Challenge not found');
      }

      // Helper functions to get target value and unit from new or legacy fields
      const getTargetValue = (challenge: any) => challenge.measurementTarget || challenge.targetValue;
      const getTargetUnit = (challenge: any) => challenge.measurementUnit || challenge.targetUnit;

      // Update progress for a specific challenge
      const targetValue = getTargetValue(challenge);
      const targetUnit = getTargetUnit(challenge);

      // Calculate new progress
      const newValue = isDailyCompletion ? value : participation.currentValue + value;
      const progress = Math.min(newValue / targetValue, 1);
      const isCompleted = progress >= 1;

      // Calculate points earned for this update (including partial points)
      const startDate = new Date(challenge.startDate);
      const endDate = new Date(challenge.endDate);
      const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      const pointsPerDay = Math.round(challenge.points / totalDays);

      // Calculate partial points based on progress percentage
      const progressPercentage = Math.min(value / targetValue, 1);
      const pointsEarned = Math.round(progressPercentage * pointsPerDay);

      // Create daily completion record
      const todayDate = (new Date().toISOString().split('T')[0]) || '1970-01-01';
      const dailyCompletion: DailyCompletion = {
        date: todayDate,
        isCompleted: value >= targetValue,
        points: pointsEarned, // Store partial points
        targetValue: targetValue,
        value,
        ...(value >= targetValue ? { completedAt: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z') } : {})
      };

      // Update participation
      const updates: Partial<ChallengeParticipation> = {
        currentValue: newValue,
        progress,
        lastUpdated: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
        status: isCompleted ? 'completed' : 'in_progress'
      };

      // Update participation-level metrics (overall challenge progress)
      if (isDailyCompletion) {
        const dailyCompletions = participation.dailyCompletions || {};
        dailyCompletions[todayDate] = dailyCompletion;

        // Calculate participation-level metrics (overall challenge progress)
        let totalPointsEarned = 0;
        let totalValue = 0;

        // Sum up all daily completions for overall metrics
        Object.values(dailyCompletions).forEach(day => {
          totalPointsEarned += day.points || 0;
          totalValue += day.value || 0;
        });

        // Calculate overall progress based on total points earned vs max possible points
        const maxPossiblePoints = challenge.points; // Total points for completing the entire challenge
        const overallProgress = maxPossiblePoints > 0 ? Math.min(totalPointsEarned / maxPossiblePoints, 1) : 0;

        // Update participation-level metrics
        updates.currentValue = totalValue; // Total cumulative value for entire challenge
        updates.pointsEarned = totalPointsEarned; // Total points earned across all days
        updates.progress = overallProgress; // Overall progress based on points earned
        updates.dailyCompletions = dailyCompletions;
      }

      if (isCompleted && !participation.completedAt) {
        updates.completedAt = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
      }

      // Note: Daily completions are already handled above in the isDailyCompletion block

      await update(participationRef, updates);
      console.log(`✅ Progress updated successfully for user ${userId}, challenge ${challengeId}`);

    } catch (error) {
      console.error('❌ Error updating progress:', error);
      throw new Error(`Failed to update progress: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get a specific challenge
   */
  async getChallenge(challengeId: string): Promise<Challenge | null> {
    try {
      const challengesRef = ref(this.database, databasePaths.challenges(this.environment));
      const snapshot = await get(challengesRef);

      if (!snapshot.exists()) {
        return null;
      }

      const data = snapshot.val();

      // Search through all challenge categories
      for (const categoryKey in data) {
        if (categoryKey.includes('challenges')) {
          const category = data[categoryKey];
          if (category[challengeId]) {
            return {
              id: challengeId,
              ...category[challengeId]
            };
          }
        }
      }

      return null;

    } catch (error) {
      console.error('❌ Error getting challenge:', error);
      throw new Error(`Failed to get challenge: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ============================================================================
  // USER OPERATIONS
  // ============================================================================

  /**
   * Load user data
   */
  async loadUser(userId: string): Promise<User | null> {
    try {
      console.log('🔄 Loading user data for:', userId);

      const userRef = ref(this.database, `${databasePaths.users(this.environment)}/${sanitizeFirebaseKey(userId)}`);
      const snapshot = await get(userRef);

      if (!snapshot.exists()) {
        console.log('⚠️ User not found:', userId);
        return null;
      }

      const userData = snapshot.val();
      const user: User = {
        id: userId,
        ...userData
      };

      // Handle profile subnode for enterprise users
      if (userData.profile) {
        user.profile = userData.profile;
      }

      console.log('✅ User data loaded successfully:', user.displayName);
      return user;

    } catch (error) {
      console.error('❌ Error loading user:', error);
      throw new Error(`Failed to load user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update user data
   */
  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    try {
      console.log('🔄 Updating user data for:', userId);

      const userRef = ref(this.database, `${databasePaths.users(this.environment)}/${sanitizeFirebaseKey(userId)}`);
      await update(userRef, updates);

      console.log('✅ User data updated successfully');

    } catch (error) {
      console.error('❌ Error updating user:', error);
      throw new Error(`Failed to update user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create or update user profile
   */
  async createUserProfile(userId: string, userData: Partial<User>): Promise<void> {
    try {
      console.log('🔄 Creating user profile for:', userId);

      // Use cloud function instead of direct database write
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const createUserProfileFn = httpsCallable(functions, 'createUserProfile');

      const userProfileData = {
        displayName: userData.displayName || '',
        email: userData.email || '',
        organizationId: userData.organizationId,
        organizationName: userData.organizationName,
        role: userData.role || 'member',
        teamId: userData.teamId,
        teamName: userData.teamName,
        authProvider: userData.authProvider || 'email',
        joinedDate: userData.joinedDate || new Date().toISOString(),
        streak: userData.streak || 0,
        totalPoints: userData.totalPoints || 0,
      };

      await createUserProfileFn({ userData: userProfileData });
      console.log('✅ User profile created successfully via cloud function');

    } catch (error) {
      console.error('❌ Error creating user profile:', error);
      throw new Error(`Failed to create user profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Find or create Firebase user by email using Firebase Auth
   */
  async findOrCreateFirebaseUser(email: string, userData: Partial<User>): Promise<string> {
    try {
      console.log('🔄 Finding or creating Firebase user for email:', email);

      // Use Firebase Auth functions
      const auth = getAuth();

      const standardPassword = 'AzureAD_Standard_Password_2024';
      let firebaseUserId = '';

      try {
        // Try to sign in first (in case user already exists)
        console.log('🔄 Attempting to sign in with existing Firebase user...');
        const authResult = await signInWithEmailAndPassword(auth, email, standardPassword);
        firebaseUserId = authResult.user.uid;
        console.log('✅ Successfully authenticated with existing Firebase user:', firebaseUserId);
      } catch (signInError: any) {
        console.log('🔄 Sign-in failed:', signInError.code);

        if (signInError.code === 'auth/user-not-found') {
          // User doesn't exist, create new user
          console.log('🆕 User not found, creating new Firebase user...');
          try {
            const authResult = await createUserWithEmailAndPassword(auth, email, standardPassword);
            firebaseUserId = authResult.user.uid;
            console.log('✅ Created new Firebase user:', firebaseUserId);

            // Update display name
            await updateProfile(authResult.user, {
              displayName: userData.displayName || email.split('@')[0],
            });
          } catch (createError: any) {
            console.log('❌ Create user failed:', createError.code);

            if (createError.code === 'auth/email-already-in-use') {
              // Email already in use, try to find existing user with different passwords
              console.log('🔄 Email already in use, trying different password variations...');

              const passwordVariations = [
                'AzureAD_Standard_Password_2024',
                'AzureAD_Standard_Password_2023',
                'AzureAD_Standard_Password',
                'Standard_Password_2024',
                'Standard_Password',
                'password123',
                'Password123',
                '123456'
              ];

              for (const password of passwordVariations) {
                try {
                  const authResult = await signInWithEmailAndPassword(auth, email, password);
                  firebaseUserId = authResult.user.uid;
                  console.log('✅ Successfully authenticated with password variation:', password);
                  break;
                } catch (error) {
                  console.log('❌ Failed to authenticate with password:', password);
                }
              }

              if (!firebaseUserId) {
                throw new Error('Could not authenticate with any password variation');
              }
            } else {
              throw createError;
            }
          }
        } else {
          throw signInError;
        }
      }

      // Create/update user profile in database using cloud function
      if (firebaseUserId) {
        try {
          // Determine default organization name based on auth provider
          let defaultOrganizationName = 'University Hospitals';
          if (userData.authProvider === 'google') {
            defaultOrganizationName = 'Individual User';
          }

          // Use cloud function instead of direct database write
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const functions = getFunctions();
          const createUserProfileFn = httpsCallable(functions, 'createUserProfile');

          const userProfileData = {
            displayName: userData.displayName || email.split('@')[0],
            email: email,
            // Don't assign organizationId for new users - they need to choose during onboarding
            organizationId: userData.organizationId || undefined,
            organizationName: userData.organizationName || undefined,
            role: userData.role || 'member',
            joinedDate: new Date().toISOString(),
            totalPoints: userData.totalPoints || 0,
            streak: userData.streak || 0,
            authProvider: userData.authProvider || 'azure',
            azureAdId: userData.id || '',
            lastAzureADSignIn: new Date().toISOString()
          };

          await createUserProfileFn({ userData: userProfileData });
          console.log('✅ User profile created/updated via cloud function:', firebaseUserId);
        } catch (error) {
          console.error('❌ Failed to create/update user profile via cloud function:', error);
          // Don't throw error here, we still have the Firebase user ID
        }
      }

      return firebaseUserId;

    } catch (error) {
      console.error('❌ Error finding or creating Firebase user:', error);
      throw new Error(`Failed to find or create Firebase user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ============================================================================
  // PARTICIPATION OPERATIONS
  // ============================================================================

  /**
   * Load user participations using Firebase user ID
   */
  async loadUserParticipations(userId: string, userData?: Partial<User>): Promise<ChallengeParticipation[]> {
    try {
      if (this.isDebugMode) {
        console.log('🔄 Loading participations for user:', userId);
      } else {
        console.log('🔄 Loading participations for user: [user ID hidden - set REACT_APP_DEBUG=true to show]');
      }
      console.log('🔍 [loadUserParticipations] Environment info:', {
        hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
        environment: this.environment,
        participationsPath: databasePaths.participations(this.environment)
      });

      // If userId looks like an email, find or create the Firebase user
      let firebaseUserId = userId;
      if (userId.includes('@')) {
        console.log('🔄 User ID is an email, finding Firebase user...');
        firebaseUserId = await this.findOrCreateFirebaseUser(userId, userData || {});
      }

      if (this.isDebugMode) {
        console.log('🔄 Using Firebase user ID for participations:', firebaseUserId);
      } else {
        console.log('🔄 Using Firebase user ID for participations: [user ID hidden - set REACT_APP_DEBUG=true to show]');
      }

      // Use original userId (without sanitization) for participations to match database rules
      const participationsRef = ref(this.database, `${databasePaths.participations(this.environment)}/${firebaseUserId}`);
      if (this.isDebugMode) {
        console.log('🔄 Firebase path:', `${databasePaths.participations(this.environment)}/${firebaseUserId}`);
      } else {
        console.log('🔄 Firebase path: [path hidden - set REACT_APP_DEBUG=true to show]');
      }

      // Check authentication state
      const auth = getAuth(this.app);
      const currentUser = auth.currentUser;
      if (this.isDebugMode) {
        console.log('🔐 [loadUserParticipations] Auth state:', {
          isAuthenticated: !!currentUser,
          currentUserId: currentUser?.uid,
          requestedUserId: firebaseUserId,
          match: currentUser?.uid === firebaseUserId
        });
      } else {
        console.log('🔐 [loadUserParticipations] Auth state: [user IDs hidden - set REACT_APP_DEBUG=true to show]');
      }

      const snapshot = await get(participationsRef);

      if (!snapshot.exists()) {
        if (this.isDebugMode) {
          console.log('⚠️ No participations found for Firebase user ID:', firebaseUserId);
        } else {
          console.log('⚠️ No participations found for Firebase user ID: [user ID hidden - set REACT_APP_DEBUG=true to show]');
        }
        return [];
      }

      const participations: ChallengeParticipation[] = [];
      const data = snapshot.val();

      if (this.isDebugMode) {
        console.log('🔄 Raw participation data:', data);
      } else {
        console.log('🔄 Raw participation data: [data hidden - set REACT_APP_DEBUG=true to show]');
      }

      for (const challengeId in data) {
        const participationData = data[challengeId];
        // Filter out abandoned participations
        if (participationData.status !== 'abandoned') {
          participations.push({
            challengeId,
            userId: firebaseUserId, // Use the Firebase user ID
            ...participationData
          });
        }
      }

      if (this.isDebugMode) {
        console.log(`✅ Loaded ${participations.length} participations for Firebase user ${firebaseUserId}`);
      } else {
        console.log(`✅ Loaded ${participations.length} participations for Firebase user [user ID hidden - set REACT_APP_DEBUG=true to show]`);
      }
      return participations;

    } catch (error) {
      console.error('❌ Error loading participations:', error);
      throw new Error(`Failed to load participations: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get participation for a specific challenge
   */
  async getParticipation(userId: string, challengeId: string): Promise<ChallengeParticipation | null> {
    try {
      // Use original userId (without sanitization) for participations to match database rules
      const participationRef = ref(this.database, `${databasePaths.participations(this.environment)}/${userId}/${challengeId}`);
      const snapshot = await get(participationRef);

      if (!snapshot.exists()) {
        return null;
      }

      return {
        challengeId,
        userId,
        ...snapshot.val()
      };

    } catch (error) {
      console.error('❌ Error getting participation:', error);
      throw new Error(`Failed to get participation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ============================================================================
  // LEADERBOARD OPERATIONS
  // ============================================================================

  /**
   * Load leaderboard for a challenge
   */
  async loadLeaderboard(challengeId: string, level: ChallengeLevel): Promise<Leaderboard | null> {
    try {
      console.log('🔄 Loading leaderboard for challenge:', challengeId);

      const leaderboardRef = ref(this.database, `${databasePaths.leaderboards(this.environment)}/${level}/${challengeId}`);
      const snapshot = await get(leaderboardRef);

      if (!snapshot.exists()) {
        console.log('⚠️ Leaderboard not found for challenge:', challengeId);
        return null;
      }

      const leaderboard: Leaderboard = {
        challengeId,
        level,
        ...snapshot.val()
      };

      console.log('✅ Leaderboard loaded successfully');
      return leaderboard;

    } catch (error) {
      console.error('❌ Error loading leaderboard:', error);
      throw new Error(`Failed to load leaderboard: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ============================================================================
  // ORGANIZATION OPERATIONS
  // ============================================================================

  /**
   * Load organization data
   */
  async loadOrganization(organizationId: string): Promise<Organization | null> {
    try {
      const orgRef = ref(this.database, `organizations/${organizationId}`);
      const snapshot = await get(orgRef);

      if (!snapshot.exists()) {
        return null;
      }

      return {
        id: organizationId,
        ...snapshot.val()
      };

    } catch (error) {
      console.error('❌ Error loading organization:', error);
      throw new Error(`Failed to load organization: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Load team data
   */
  async loadTeam(teamId: string): Promise<Team | null> {
    try {
      const teamRef = ref(this.database, `organizations/${teamId}`);
      const snapshot = await get(teamRef);

      if (!snapshot.exists()) {
        return null;
      }

      return {
        id: teamId,
        ...snapshot.val()
      };

    } catch (error) {
      console.error('❌ Error loading team:', error);
      throw new Error(`Failed to load team: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ============================================================================
  // HEALTH DATA OPERATIONS
  // ============================================================================

  /**
   * Save health data
   */
  async saveHealthData(healthData: HealthData): Promise<void> {
    try {
      const healthRef = ref(this.database, `health_data/${sanitizeFirebaseKey(healthData.userId)}/${healthData.date}`);
      await set(healthRef, healthData);
    } catch (error) {
      console.error('❌ Error saving health data:', error);
      throw new Error(`Failed to save health data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Load health data for a user and date range
   */
  async loadHealthData(userId: string, startDate: string, endDate: string): Promise<HealthData[]> {
    try {
      const healthRef = ref(this.database, `health_data/${sanitizeFirebaseKey(userId)}`);
      const snapshot = await get(healthRef);

      if (!snapshot.exists()) {
        return [];
      }

      const data = snapshot.val();
      const healthData: HealthData[] = [];

      for (const date in data) {
        if (date >= startDate && date <= endDate) {
          healthData.push({
            userId,
            date,
            ...data[date]
          });
        }
      }

      return healthData.sort((a, b) => a.date.localeCompare(b.date));

    } catch (error) {
      console.error('❌ Error loading health data:', error);
      throw new Error(`Failed to load health data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ============================================================================
  // REAL-TIME LISTENERS
  // ============================================================================

  /**
   * Listen to user data changes
   */
  listenToUser(userId: string, callback: (user: User | null) => void): () => void {
    const userRef = ref(this.database, `${databasePaths.users(this.environment)}/${sanitizeFirebaseKey(userId)}`);
    const listener = onValue(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.val();
        const user: User = {
          id: userId,
          ...userData
        };
        callback(user);
      } else {
        callback(null);
      }
    });

    this.listeners.set(`user_${userId}`, () => off(userRef, 'value', listener));
    return () => this.removeListener(`user_${userId}`);
  }

  /**
   * Listen to user participations
   */
  listenToParticipations(userId: string, callback: (participations: ChallengeParticipation[]) => void): () => void {
    const participationsRef = ref(this.database, `participations/${sanitizeFirebaseKey(userId)}`);
    const listener = onValue(participationsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const participations: ChallengeParticipation[] = [];

        for (const challengeId in data) {
          participations.push({
            challengeId,
            userId,
            ...data[challengeId]
          });
        }

        callback(participations);
      } else {
        callback([]);
      }
    });

    this.listeners.set(`participations_${userId}`, () => off(participationsRef, 'value', listener));
    return () => this.removeListener(`participations_${userId}`);
  }

  /**
   * Listen to leaderboard changes
   */
  listenToLeaderboard(challengeId: string, level: ChallengeLevel, callback: (leaderboard: Leaderboard | null) => void): () => void {
    const leaderboardRef = ref(this.database, `leaderboards/${level}/${challengeId}`);
    const listener = onValue(leaderboardRef, (snapshot) => {
      if (snapshot.exists()) {
        const leaderboard: Leaderboard = {
          challengeId,
          level,
          ...snapshot.val()
        };
        callback(leaderboard);
      } else {
        callback(null);
      }
    });

    this.listeners.set(`leaderboard_${challengeId}`, () => off(leaderboardRef, 'value', listener));
    return () => this.removeListener(`leaderboard_${challengeId}`);
  }

  /**
   * Remove a specific listener
   */
  private removeListener(key: string): void {
    const removeFn = this.listeners.get(key);
    if (removeFn) {
      removeFn();
      this.listeners.delete(key);
    }
  }

  /**
   * Remove all listeners
   */
  removeAllListeners(): void {
    this.listeners.forEach((removeFn) => removeFn());
    this.listeners.clear();
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get database reference
   */
  getDatabaseRef(): DatabaseReference {
    return this.ref;
  }

  /**
   * Get database instance
   */
  getDatabase(): Database {
    return this.database;
  }

  /**
   * Get Firebase app instance
   */
  getApp(): FirebaseApp {
    return this.app;
  }

  /**
   * Call Firebase Cloud Function
   */
  async callFunction(functionName: string, data: any): Promise<any> {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const callableFunction = httpsCallable(functions, functionName);

      const result = await callableFunction(data);
      return result.data;
    } catch (error) {
      console.error(`❌ Error calling function ${functionName}:`, error);
      throw new Error(`Failed to call function ${functionName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if Firebase is connected
   */
  async isConnected(): Promise<boolean> {
    try {
      const connectedRef = ref(this.database, '.info/connected');
      const snapshot = await get(connectedRef);
      return snapshot.val() === true;
    } catch {
      return false;
    }
  }

  /**
   * Update user notification settings
   */
  async updateUserNotificationSettings(userId: string, settings: {
    challengeReminders: boolean;
    leaderboardUpdates: boolean;
    achievementCelebrations: boolean;
    dailyProgress: boolean;
  }): Promise<void> {
    try {
      const userRef = ref(this.database, `users/${userId}`);
      await update(userRef, {
        notificationSettings: settings,
        lastUpdated: new Date().toISOString()
      });

      if (this.isDebugMode) {
        console.log('🔔 FirebaseService: Notification settings updated for user:', userId, settings);
      }
    } catch (error) {
      console.error('❌ FirebaseService: Failed to update notification settings:', error);
      throw error;
    }
  }

  /**
   * Get user health settings
   */
  async getUserHealthSettings(userId: string): Promise<{
    healthKitEnabled?: boolean;
    autoSyncEnabled?: boolean;
    showHealthData?: boolean;
    autoUpdateChallenges?: boolean;
    syncInterval?: number;
    backgroundSync?: boolean;
    challengeReminders?: boolean;
    leaderboardUpdates?: boolean;
  } | null> {
    try {
      const userRef = ref(this.database, `users/${userId}`);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        const userData = snapshot.val();
        const healthSettings = {
          healthKitEnabled: userData.healthKitEnabled,
          autoSyncEnabled: userData.autoSyncEnabled,
          showHealthData: userData.showHealthData,
          autoUpdateChallenges: userData.autoUpdateChallenges,
          syncInterval: userData.syncInterval,
          backgroundSync: userData.backgroundSync,
          challengeReminders: userData.challengeReminders,
          leaderboardUpdates: userData.leaderboardUpdates
        };

        if (this.isDebugMode) {
          console.log('🔧 FirebaseService: Health settings loaded for user:', userId, healthSettings);
        }
        return healthSettings;
      } else {
        if (this.isDebugMode) {
          console.log('ℹ️ FirebaseService: No user data found for health settings');
        }
        return null;
      }
    } catch (error) {
      console.error('❌ FirebaseService: Failed to get health settings:', error);
      return null;
    }
  }

  /**
   * Check if a user is a family member of someone in the given organization
   * This method is simplified to use existing user data instead of making additional database calls
   */
  private async isFamilyMemberOfOrganization(userId: string, familyMemberOf: string, organizationId: string): Promise<boolean> {
    try {
      console.log('[FirebaseService] isFamilyMemberOfOrganization - checking:', { userId, familyMemberOf, organizationId });

      // Since the user object already contains familyMemberOf, we can check if the organizationId matches
      // This is a simplified check - if the user is a family member, they should have access to their family member's organization
      // The actual organization validation should be done based on the familyMemberOf user's organization

      // For now, return true if the user is a family member (familyMemberOf is set)
      // This allows family members to access challenges from their family member's organization
      const isFamilyMember = !!familyMemberOf;

      console.log('[FirebaseService] isFamilyMemberOfOrganization - simplified check result:', isFamilyMember);
      return isFamilyMember;
    } catch (error) {
      console.error('❌ FirebaseService: Error checking family member organization:', error);
      return false;
    }
  }
} 